import { Fragment, useEffect } from "react";
import { MapContainer, TileLayer, Rectangle, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useQuery } from "@tanstack/react-query";
import { fetchNinoSSTs, ensoStatus, anomalyColor, ninoRegions } from "@/lib/elnino";
import { Loader2, Thermometer, Activity } from "lucide-react";

// Fix default icon paths in vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export const ElNinoMap = () => {
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ["enso-ssts"],
    queryFn: fetchNinoSSTs,
    refetchInterval: 15 * 60 * 1000,
  });

  useEffect(() => {
    document.title = "El Niño tracker — Swell Atlas";
  }, []);

  const nino34 = data?.find((d) => d.region.id === "nino34");
  const status = nino34 ? ensoStatus(nino34.anomaly) : null;

  return (
    <div className="space-y-6">
      {/* Status banner */}
      <div className="rounded-3xl bg-card/80 backdrop-blur-md border border-border p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Equatorial Pacific</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-foreground mt-1">El Niño Tracker</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Live sea surface temperatures across the four NOAA ENSO monitoring regions.
              Anomalies vs. 1981–2010 climatology drive El Niño / La Niña classification.
            </p>
          </div>
          {status && nino34 && (
            <div className="flex flex-col items-end">
              <span
                className="px-4 py-2 rounded-full text-sm font-bold text-accent-foreground"
                style={{ background: anomalyColor(nino34.anomaly) }}
              >
                {status.label}
              </span>
              <span className="text-xs text-muted-foreground mt-2">
                Niño 3.4 anomaly: <strong className="text-foreground">{nino34.anomaly >= 0 ? "+" : ""}{nino34.anomaly.toFixed(2)}°C</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="rounded-3xl overflow-hidden border border-border bg-ocean-deep relative" style={{ height: "26rem" }}>
        {isLoading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-ocean-deep/60 text-foam">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Reading the Pacific…
          </div>
        )}
        <MapContainer
          center={[0, -150]}
          zoom={2}
          minZoom={2}
          worldCopyJump
          className="h-full w-full"
          style={{ background: "hsl(var(--ocean-deep))" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {(data ?? ninoRegions.map(r => ({ region: r, sst: 0, anomaly: 0 }))).map(({ region, sst, anomaly }) => {
            const color = data ? anomalyColor(anomaly) : "hsl(200 30% 50%)";
            return (
              <Fragment key={region.id}>
                <Rectangle
                  bounds={region.bounds}
                  pathOptions={{ color, weight: 2, fillOpacity: 0.35 }}
                >
                  <Tooltip direction="top" sticky>
                    <strong>{region.name}</strong>
                    {data && <><br />SST: {sst.toFixed(2)}°C<br />Anomaly: {anomaly >= 0 ? "+" : ""}{anomaly.toFixed(2)}°C</>}
                  </Tooltip>
                </Rectangle>
                {data && (
                  <Marker position={[region.lat, region.lon]}>
                    <Popup>
                      <div style={{ minWidth: 160 }}>
                        <strong>{region.name}</strong>
                        <div style={{ fontSize: 12, opacity: 0.8 }}>{region.description}</div>
                        <div style={{ marginTop: 6 }}>SST: <strong>{sst.toFixed(2)}°C</strong></div>
                        <div>Anomaly: <strong style={{ color }}>{anomaly >= 0 ? "+" : ""}{anomaly.toFixed(2)}°C</strong></div>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </Fragment>
            );
          })}
        </MapContainer>
      </div>

      {/* Region cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {(data ?? []).map(({ region, sst, anomaly }) => {
          const color = anomalyColor(anomaly);
          return (
            <div key={region.id} className="rounded-2xl bg-card/80 border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{region.name}</div>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <Thermometer className="h-4 w-4 text-primary" />
                <span className="text-2xl font-bold text-foreground">{sst.toFixed(1)}°C</span>
              </div>
              <div className="mt-1 text-sm font-bold" style={{ color }}>
                {anomaly >= 0 ? "+" : ""}{anomaly.toFixed(2)}°C anomaly
              </div>
              <p className="text-xs text-muted-foreground mt-2 leading-snug">{region.description}</p>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="rounded-2xl bg-card/60 border border-border p-4 flex flex-wrap items-center gap-3 text-xs">
        <span className="font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <Activity className="h-3 w-3" /> ENSO scale
        </span>
        {[
          { l: "Strong La Niña", a: -2 },
          { l: "La Niña", a: -1 },
          { l: "Neutral", a: 0 },
          { l: "El Niño", a: 1 },
          { l: "Strong El Niño", a: 2 },
        ].map((s) => (
          <span key={s.l} className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full" style={{ background: anomalyColor(s.a) }} />
            {s.l}
          </span>
        ))}
        <span className="ml-auto text-muted-foreground">
          {dataUpdatedAt ? `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}` : ""}
        </span>
      </div>

      {isError && (
        <div className="rounded-2xl bg-destructive/10 border border-destructive/30 p-4 text-destructive text-sm">
          Couldn't fetch live SST data. Try again in a moment.
        </div>
      )}
    </div>
  );
};
